export type MasterTypeStatus =
  | "supported"
  | "alias"
  | "hidden"
  | "dedicated-controller"
  | "backend-missing"
  | "backend-internal"
  | "not-required"
  | "unknown-needs-review";

export interface MasterTypeRegistryEntry {
  readonly canonicalMasterType: string;
  readonly status: MasterTypeStatus;
  readonly backendListEndpoint?: string | undefined;
  readonly backendDropdownEndpoint?: string | undefined;
  readonly backendCreateEndpoint?: string | undefined;
  readonly backendUpdateEndpoint?: string | undefined;
  readonly backendDeactivateEndpoint?: string | undefined;
  readonly backendReactivateEndpoint?: string | undefined;
  readonly backendDeleteEndpoint?: string | undefined;
  readonly frontendRoute?: string | undefined;
  readonly pageComponent?: string | undefined;
  readonly permissionCode?: string | undefined;
  readonly exposedInMenu: boolean;
  readonly usesGenericAdminMasterApi: boolean;
  readonly usesDedicatedController: boolean;
  readonly notes?: string | undefined;
}

export interface MasterTypeAliasEntry {
  readonly alias: string;
  readonly canonicalMasterType: string;
  readonly reason: string;
  readonly active: boolean;
  readonly safeToResolve: boolean;
}

type SupportedMasterSeed = {
  readonly masterType: string;
  readonly exposedInMenu?: boolean | undefined;
  readonly frontendRoute?: string | undefined;
  readonly pageComponent?: string | undefined;
  readonly permissionCode?: string | undefined;
  readonly notes?: string | undefined;
};

const ADMIN_BASE = "/admin";

const BACKEND_SUPPORTED_MASTER_TYPES: readonly SupportedMasterSeed[] = [
  { masterType: "complaint-source", frontendRoute: "/cmts/admin/master-data/complaint-sources", pageComponent: "CmtsComplaintSourcePage", exposedInMenu: true },
  { masterType: "complaint-type", frontendRoute: "/cmts/admin/master-data/complaint-types", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  {
  masterType: "capa-status",
  frontendRoute: "/cmts/admin/master-data/capa-statuses",
  pageComponent: "CmtsMasterDataAdminPage",
  exposedInMenu: true,
},
  { masterType: "complaint-category", frontendRoute: "/cmts/admin/master-data/complaint-categories", pageComponent: "CmtsComplaintCategoryPage", exposedInMenu: true },
  { masterType: "complaint-subcategory", frontendRoute: "/cmts/admin/master-data/complaint-subcategories", pageComponent: "CmtsComplaintSubcategoryPage", exposedInMenu: true },
  { masterType: "priority", frontendRoute: "/cmts/admin/master-data/priorities", pageComponent: "CmtsPriorityPage", exposedInMenu: true },
  { masterType: "severity", frontendRoute: "/cmts/admin/master-data/severities", pageComponent: "CmtsSeverityPage", exposedInMenu: true },
  { masterType: "sla-state" },
  { masterType: "confidentiality-level" },
  { masterType: "workflow-profile" },
  { masterType: "workflow-status", frontendRoute: "/cmts/admin/master-data/workflow-statuses", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "workflow-action" },
  { masterType: "workflow-transition" },
  { masterType: "workflow-stage", frontendRoute: "/cmts/admin/master-data/workflow-stages", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "rca-method", frontendRoute: "/cmts/admin/master-data/rca-methods", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "root-cause-category", frontendRoute: "/cmts/admin/master-data/root-cause-categories", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "capa-action-type", frontendRoute: "/cmts/admin/master-data/capa-action-types", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "verification-method", frontendRoute: "/cmts/admin/master-data/verification-methods", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "verification-rule", frontendRoute: "/cmts/admin/master-data/verification-rules", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "effectiveness-status", frontendRoute: "/cmts/admin/master-data/effectiveness-statuses", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "verification-status" },
  { masterType: "evidence-type", frontendRoute: "/cmts/admin/master-data/evidence-types", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "user-type" },
  { masterType: "organization-unit-type" },
  { masterType: "role-type" },
  { masterType: "permission-group" },
  { masterType: "tenant" },
  { masterType: "user" },
  { masterType: "role" },
  { masterType: "permission" },
  { masterType: "defect-mode", frontendRoute: "/cmts/admin/master-data/defect-modes", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "failure-mode", frontendRoute: "/cmts/admin/master-data/failure-modes", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "containment-types", frontendRoute: "/cmts/admin/master-data/containment-types", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "reason-code", frontendRoute: "/cmts/admin/master-data/reason-codes", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "closure-code", frontendRoute: "/cmts/admin/master-data/closure-codes", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "departments", frontendRoute: "/cmts/admin/master-data/departments", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "sites", frontendRoute: "/cmts/admin/master-data/sites", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "product-family", frontendRoute: "/cmts/admin/master-data/product-families", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "products", frontendRoute: "/cmts/admin/master-data/products", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "parts", frontendRoute: "/cmts/admin/master-data/parts", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "processes", frontendRoute: "/cmts/admin/master-data/processes", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "lines", frontendRoute: "/cmts/admin/master-data/lines", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "machines", frontendRoute: "/cmts/admin/master-data/machines", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "notificationTemplates", frontendRoute: "/cmts/admin/master-data/notification-templates", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "messageTemplates", frontendRoute: "/cmts/admin/master-data/message-templates", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "report-definition", frontendRoute: "/cmts/admin/master-data/report-types", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "dashboard-kpi", frontendRoute: "/cmts/admin/master-data/dashboard-kpis", pageComponent: "CmtsMasterDataAdminPage", exposedInMenu: true },
  { masterType: "calendar-event-type" },
  { masterType: "calendarRules", frontendRoute: "/cmts/admin/calendar-scheduler", pageComponent: "CmtsAdminCalendarSchedulerContainer", exposedInMenu: false },
  { masterType: "evidencePolicies", frontendRoute: "/cmts/admin/evidence", pageComponent: "CmtsAdminEvidenceContainer", exposedInMenu: false },
  { masterType: "fieldSchemas", frontendRoute: "/cmts/admin/field-schemas", pageComponent: "CmtsFieldSchemaAdminContainer", exposedInMenu: false },
  { masterType: "scheduledJobs", frontendRoute: "/cmts/admin/scheduled-jobs", pageComponent: "CmtsScheduledJobsContainer", exposedInMenu: false },
  { masterType: "tags" },
  { masterType: "reference-values", frontendRoute: "/cmts/admin/master-data/reference-data", pageComponent: "CmtsReferenceDataPage", exposedInMenu: true },
  { masterType: "escalation-rules" },
  { masterType: "configuration" },
  { masterType: "governance" },
  { masterType: "routing", frontendRoute: "/cmts/admin/routing", pageComponent: "CmtsRoutingAdminContainer", exposedInMenu: true },
  { masterType: "sod", frontendRoute: "/cmts/admin/sod", pageComponent: "CmtsSodAdminContainer", exposedInMenu: true, notes: "Backend proves generic /api/cmts/admin/sod and dedicated read-only /api/cmts/sod/policies." },
];

export const CMTS_MASTER_TYPE_ALIASES_CANONICAL: readonly MasterTypeAliasEntry[] = [
  { alias: "complaintSource", canonicalMasterType: "complaint-source", reason: "legacy camelCase", active: true, safeToResolve: true },
  { alias: "complaint_source", canonicalMasterType: "complaint-source", reason: "legacy snake_case", active: true, safeToResolve: true },
  { alias: "complaint-sources", canonicalMasterType: "complaint-source", reason: "route plural", active: true, safeToResolve: true },
  { alias: "complaint-category-master", canonicalMasterType: "complaint-category", reason: "legacy page config", active: true, safeToResolve: true },
  { alias: "categories", canonicalMasterType: "complaint-category", reason: "legacy category alias", active: true, safeToResolve: true },
  { alias: "subcategories", canonicalMasterType: "complaint-subcategory", reason: "legacy subcategory alias", active: true, safeToResolve: true },
  { alias: "workflowStatus", canonicalMasterType: "workflow-status", reason: "legacy camelCase", active: true, safeToResolve: true },
  { alias: "workflow_status", canonicalMasterType: "workflow-status", reason: "legacy snake_case", active: true, safeToResolve: true },
  { alias: "workflow-statuses", canonicalMasterType: "workflow-status", reason: "route plural", active: true, safeToResolve: true },
  { alias: "workflowStage", canonicalMasterType: "workflow-stage", reason: "legacy camelCase", active: true, safeToResolve: true },
  { alias: "workflow-stages", canonicalMasterType: "workflow-stage", reason: "route plural", active: true, safeToResolve: true },
  { alias: "root-cause-categories", canonicalMasterType: "root-cause-category", reason: "route plural", active: true, safeToResolve: true },
  { alias: "capa-action-types", canonicalMasterType: "capa-action-type", reason: "route plural", active: true, safeToResolve: true },
  {
  alias: "capa-statuses",
  canonicalMasterType: "capa-status",
  reason: "route plural",
  active: true,
  safeToResolve: true,
},
  { alias: "verification-methods", canonicalMasterType: "verification-method", reason: "route plural", active: true, safeToResolve: true },
  { alias: "evidence-types", canonicalMasterType: "evidence-type", reason: "route plural", active: true, safeToResolve: true },
  { alias: "notification-template", canonicalMasterType: "notificationTemplates", reason: "frontend singular", active: true, safeToResolve: true },
  { alias: "notification-templates", canonicalMasterType: "notificationTemplates", reason: "frontend hyphen plural", active: true, safeToResolve: true },
  { alias: "notificationTemplate", canonicalMasterType: "notificationTemplates", reason: "legacy camelCase", active: true, safeToResolve: true },
  { alias: "message-template", canonicalMasterType: "messageTemplates", reason: "frontend singular", active: true, safeToResolve: true },
  { alias: "message-templates", canonicalMasterType: "messageTemplates", reason: "frontend hyphen plural", active: true, safeToResolve: true },
  { alias: "messageTemplate", canonicalMasterType: "messageTemplates", reason: "legacy camelCase", active: true, safeToResolve: true },
  { alias: "report-type", canonicalMasterType: "report-definition", reason: "frontend generic report type", active: true, safeToResolve: true },
  { alias: "report-types", canonicalMasterType: "report-definition", reason: "frontend route plural", active: true, safeToResolve: true },
  { alias: "report-configurations", canonicalMasterType: "report-definition", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "dashboard-kpis", canonicalMasterType: "dashboard-kpi", reason: "route plural", active: true, safeToResolve: true },
  { alias: "reference-data", canonicalMasterType: "reference-values", reason: "frontend label maps to backend reference-values", active: true, safeToResolve: true },
  { alias: "referenceData", canonicalMasterType: "reference-values", reason: "admin config camelCase", active: true, safeToResolve: true },
  { alias: "reference-values", canonicalMasterType: "reference-values", reason: "backend canonical", active: true, safeToResolve: true },
  { alias: "reference-type", canonicalMasterType: "reference-type", reason: "dedicated ReferenceTypesController compatibility surface; not generic /admin/reference-type", active: true, safeToResolve: true },
  { alias: "reference-types", canonicalMasterType: "reference-type", reason: "dedicated ReferenceTypesController route plural; not generic /admin/reference-type", active: true, safeToResolve: true },
  { alias: "department", canonicalMasterType: "departments", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "site", canonicalMasterType: "sites", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "product", canonicalMasterType: "products", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "part", canonicalMasterType: "parts", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "process", canonicalMasterType: "processes", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "line", canonicalMasterType: "lines", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "machine", canonicalMasterType: "machines", reason: "legacy singular", active: true, safeToResolve: true },
  { alias: "calendar-rules", canonicalMasterType: "calendarRules", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "calendars", canonicalMasterType: "calendarRules", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "evidence-policies", canonicalMasterType: "evidencePolicies", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "field-schemas", canonicalMasterType: "fieldSchemas", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "scheduled-jobs", canonicalMasterType: "scheduledJobs", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "sod-policies", canonicalMasterType: "sod", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "routing-rules", canonicalMasterType: "routing", reason: "backend compatibility alias", active: true, safeToResolve: true },
  { alias: "confidentiality", canonicalMasterType: "confidentiality-level", reason: "frontend shorthand used by evidence/complaint dropdowns", active: true, safeToResolve: true },
];

export const CMTS_UNSUPPORTED_MASTER_TYPE_REGISTRY: readonly MasterTypeRegistryEntry[] = [
  {
    canonicalMasterType: "reference-type",
    status: "dedicated-controller",
    frontendRoute: "/cmts/admin/master-data/reference-types",
    pageComponent: "ReferenceTypePage",
    permissionCode: "AUTH.USER_MANAGE",
    exposedInMenu: true,
    usesGenericAdminMasterApi: false,
    usesDedicatedController: true,
    backendListEndpoint: "/reference-types",
    backendDropdownEndpoint: "/reference-types",
    backendCreateEndpoint: "/reference-types",
    backendUpdateEndpoint: "/reference-types/{id}",
    backendDeactivateEndpoint: "/reference-types/{id}/deactivate",
    backendReactivateEndpoint: "/reference-types/{id}/activate",
    backendDeleteEndpoint: "/reference-types/{id}",
    notes: "Backend exposes dedicated /api/cmts/reference-types compatibility CRUD. Generic /api/cmts/admin/reference-type is not supported by CanonicalMasterDataAdminService.",
  },
  {
    canonicalMasterType: "organization",
    status: "backend-missing",
    frontendRoute: "/cmts/admin/organization",
    pageComponent: "CmtsOrganizationAdminContainer",
    permissionCode: "AUTH.USER_MANAGE",
    exposedInMenu: true,
    usesGenericAdminMasterApi: false,
    usesDedicatedController: true,
    notes: "Backend has dedicated organization-units routes and tree/read APIs; generic /api/cmts/admin/organization is not supported and must not be called.",
  },
];

const supportedEntries = BACKEND_SUPPORTED_MASTER_TYPES.map((item): MasterTypeRegistryEntry => ({
  canonicalMasterType: item.masterType,
  status: "supported",
  backendListEndpoint: `${ADMIN_BASE}/${item.masterType}`,
  backendDropdownEndpoint: `${ADMIN_BASE}/${item.masterType}/dropdown-options`,
  backendCreateEndpoint: `${ADMIN_BASE}/${item.masterType}`,
  backendUpdateEndpoint: `${ADMIN_BASE}/${item.masterType}/{id}`,
  backendDeactivateEndpoint: `${ADMIN_BASE}/${item.masterType}/{id}/deactivate`,
  backendReactivateEndpoint: `${ADMIN_BASE}/${item.masterType}/{id}/activate`,
  backendDeleteEndpoint: `${ADMIN_BASE}/${item.masterType}/{id}`,
  frontendRoute: item.frontendRoute,
  pageComponent: item.pageComponent,
  permissionCode: item.permissionCode ?? "AUTH.USER_MANAGE",
  exposedInMenu: item.exposedInMenu ?? false,
  usesGenericAdminMasterApi: true,
  usesDedicatedController: false,
  notes: item.notes,
}));

export const CMTS_MASTER_TYPE_REGISTRY = Object.freeze([...supportedEntries, ...CMTS_UNSUPPORTED_MASTER_TYPE_REGISTRY]) as readonly MasterTypeRegistryEntry[];
export const supportedGenericMasterTypes = Object.freeze(supportedEntries.map((entry) => entry.canonicalMasterType)) as readonly string[];

const registryByCanonical = new Map<string, MasterTypeRegistryEntry>(CMTS_MASTER_TYPE_REGISTRY.map((entry) => [keyOf(entry.canonicalMasterType), entry]));
const aliasByKey = new Map<string, MasterTypeAliasEntry>(CMTS_MASTER_TYPE_ALIASES_CANONICAL.map((entry) => [keyOf(entry.alias), entry]));

function keyOf(input: string | undefined): string {
  return String(input ?? "").trim().replace(/_/g, "-").toLowerCase();
}

export function normalizeMasterType(input: string): string {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) return "";
  const direct = registryByCanonical.get(keyOf(trimmed));
  if (direct) return direct.canonicalMasterType;
  const alias = aliasByKey.get(keyOf(trimmed));
  if (alias?.active && alias.safeToResolve) return alias.canonicalMasterType;
  return trimmed;
}

export function resolveMasterTypeEntry(input: string): MasterTypeRegistryEntry {
  const canonical = normalizeMasterType(input);
  const entry = registryByCanonical.get(keyOf(canonical));
  if (entry) return entry;
  return {
    canonicalMasterType: canonical || String(input ?? ""),
    status: "unknown-needs-review",
    exposedInMenu: false,
    usesGenericAdminMasterApi: false,
    usesDedicatedController: false,
    notes: `Master type '${input}' is not present in the canonical frontend/backend registry.`,
  };
}

export function isSupportedGenericMasterType(input: string): boolean {
  const entry = resolveMasterTypeEntry(input);
  return entry.status === "supported" && entry.usesGenericAdminMasterApi;
}

export class UnsupportedMasterTypeError extends Error {
  readonly masterType: string;
  readonly normalizedMasterType: string;
  readonly status: MasterTypeStatus;

  constructor(input: { readonly masterType: string; readonly normalizedMasterType: string; readonly status: MasterTypeStatus; readonly message: string }) {
    super(input.message);
    this.name = "UnsupportedMasterTypeError";
    this.masterType = input.masterType;
    this.normalizedMasterType = input.normalizedMasterType;
    this.status = input.status;
  }
}

export function assertSupportedGenericMasterType(input: string): void {
  const entry = resolveMasterTypeEntry(input);
  if (entry.status === "supported" && entry.usesGenericAdminMasterApi) return;
  throw new UnsupportedMasterTypeError({
    masterType: input,
    normalizedMasterType: entry.canonicalMasterType,
    status: entry.status,
    message: `Unsupported masterType '${input}' for generic admin master-data API. Registry status: ${entry.status}; normalized: ${entry.canonicalMasterType}.`,
  });
}

export function shouldExposeMasterTypeInMenu(input: string): boolean {
  return resolveMasterTypeEntry(input).exposedInMenu;
}

export function isDedicatedControllerMasterType(input: string): boolean {
  return resolveMasterTypeEntry(input).usesDedicatedController;
}

export function canonicalMasterTypeForGenericApi(input: string): string {
  assertSupportedGenericMasterType(input);
  return resolveMasterTypeEntry(input).canonicalMasterType;
}
